import type { BarInterval, BarPoint } from '../../api/marketData'

/**
 * Mapping from chart interval to moving-average/session config bucket.
 * Adding `30m` or `4h` later is a single-line change.
 */
export function intervalToBucket(interval: BarInterval): 'intraday' | 'hourly' | 'daily' | 'weekly' {
  if (interval === '1h') return 'hourly'
   if (interval === '1d') return 'daily'
   if (interval === '1w') return 'weekly'
  // '1s' is coerced to '1m' upstream; treat as intraday.
  return 'intraday'
}

// ── Indicator output primitives ────────────────────────────────────────────

export interface IndicatorLine {
  id: string
  title: string
  color: string
  width: number
  style?: 'solid' | 'dashed' | 'dotted'
  points: Array<{ t: number; value: number }>
}

export interface IndicatorPriceLevel {
  id: string
  title: string
  color: string
  width: number
  style?: 'solid' | 'dashed' | 'dotted'
  /** When false, no line series is drawn — only the right-edge label is emitted. Default: true. */
  lineVisible?: boolean
  segments: Array<{
    fromT: number
    toT: number
    value: number
  }>
}

/**
 * Time-and-price-bounded rectangle. Use for ORB area fill and any
 * candle-background highlights (one band per highlighted bar, with
 * `fromT`/`toT` set to the bar's open and close timestamps).
 */
export interface IndicatorBand {
  id: string
  fromT: number
  toT: number
  topValue: number
  bottomValue: number
  color: string
  opacity: number
}

export interface IndicatorLabel {
  id: string
  t: number
  value: number
  text: string
  color: string
  /** Vertical anchor relative to `value`. Default: 'above'. */
  anchor?: 'above' | 'below'
  /** Horizontal alignment around `t`. Default: 'center'. */
  align?: 'left' | 'center' | 'right'
}

export interface IndicatorOutput {
  lines: IndicatorLine[]
  priceLevels: IndicatorPriceLevel[]
  bands: IndicatorBand[]
  labels: IndicatorLabel[]
}

// ── Indicator context ──────────────────────────────────────────────────────

export interface IndicatorContext {
  symbol: string
  interval: BarInterval
  bars: BarPoint[]
  /** IANA timezone for all session logic. Always 'America/New_York' in v1. */
  timezone: string
  nowMs: number
  /**
   * Whether the last bar in `bars` is still forming. When true, the math
   * layer must exclude the last bar from "completed" calculations
   * (EMA/SMA values plotted on closed bars, ORB breakout detection,
   * completed-session percent labels). The renderer may still draw a
   * provisional value for the active bar where it makes sense
   * (e.g. VWAP-to-now, active session percent label).
   */
  treatLastBarAsClosed: boolean
  /**
   * Auxiliary bars covering only the previous trading session. Used by
   * computePrevDayHighLow when the chart's `bars` window doesn't include
   * yesterday (e.g. range='1D' returns today only). When non-empty, PDH/PDL
   * is derived from these bars directly instead of scanning `bars` for a
   * prior date with complete RTH coverage.
   */
  prevDayBars?: BarPoint[]
}

// ── User indicator types ───────────────────────────────────────────────────

export type IndicatorType = 'ema' | 'sma' | 'vwap' | 'premarketHighLow' | 'prevDayHighLow' | 'orb' | 'volume'

export interface UserEmaIndicator {
  id: string
  type: 'ema'
  enabled: boolean
  period: number
  color: string
  width: number
  style?: 'solid' | 'dashed' | 'dotted'
}

export interface UserSmaIndicator {
  id: string
  type: 'sma'
  enabled: boolean
  period: number
  color: string
  width: number
  style?: 'solid' | 'dashed' | 'dotted'
}

export interface UserVwapIndicator {
  id: string
  type: 'vwap'
  enabled: boolean
  source: 'hlc3' | 'close'
  color: string
  width: number
  style?: 'solid' | 'dashed' | 'dotted'
}

export interface UserPremarketHighLowIndicator {
  id: string
  type: 'premarketHighLow'
  enabled: boolean
  /** Color for the premarket high line. */
  highColor: string
  /** Color for the premarket low line. */
  lowColor: string
  width?: number
  style?: 'solid' | 'dashed' | 'dotted'
}

export interface UserPrevDayHighLowIndicator {
  id: string
  type: 'prevDayHighLow'
  enabled: boolean
  /** Color for the previous-day high line. */
  highColor: string
  /** Color for the previous-day low line. */
  lowColor: string
  width?: number
  style?: 'solid' | 'dashed' | 'dotted'
}

export interface UserOrbIndicator {
  id: string
  type: 'orb'
  enabled: boolean
  /** Color for the ORB high line. */
  highColor: string
  /** Color for the ORB low line. */
  lowColor: string
  width?: number
  style?: 'solid' | 'dashed' | 'dotted'
  /** When true (default), render a shaded rectangle between ORB high and low. */
  shaded?: boolean
}

/**
 * Volume histogram pinned to the bottom of the price pane (overlay scale —
 * the candles float above it). Unlike the line/level indicators it isn't
 * computed by the engine; `TickerPriceChart` renders it directly off each
 * bar's `v`, coloring by candle direction. This entry only carries the
 * on/off toggle + colors so it shows up in the indicator menu and persists.
 */
export interface UserVolumeIndicator {
  id: string
  type: 'volume'
  enabled: boolean
  /** Bar color when close ≥ open. */
  upColor: string
  /** Bar color when close < open. */
  downColor: string
}

export type UserIndicator =
  | UserEmaIndicator
  | UserSmaIndicator
  | UserVwapIndicator
  | UserPremarketHighLowIndicator
  | UserPrevDayHighLowIndicator
  | UserOrbIndicator
  | UserVolumeIndicator

export interface UserIndicatorConfig {
  version: number
  indicators: UserIndicator[]
}

// ── Default config types (reference only; not used in v1) ──────────────────

export interface MovingAverageSpec {
  id: string
  kind: 'ema' | 'sma'
  length: number
  title: string
  color: string
  width: number
}

/**
 * @deprecated Superseded by the per-individual-indicator `UserIndicator` model.
 * Kept here for reference in the design spec.
 */
export interface SessionMaIndicatorConfig {
  enabled: boolean
  movingAverages: {
    weekly: MovingAverageSpec[]
    daily: MovingAverageSpec[]
    hourly: MovingAverageSpec[]
    intraday: MovingAverageSpec[]
  }
  fills: Array<{
    fastId: string
    slowId: string
    bullColor: string
    bearColor: string
    opacity: number
  }>
  vwap: {
    enabled: boolean
    source: 'hlc3' | 'close'
    color: string
    width: number
  }
  orb: {
    enabled: boolean
    openingRange: string
    regularHours: string
    lineColor: string
    fillColor: string
    fillOpacity: number
    requireVolume: boolean
    volumeMultiplier: number
  }
  previousDay: {
    enabled: boolean
  }
  premarket: {
    enabled: boolean
    session: string
  }
  sessionPercentChange: {
    enabled: boolean
    intervals: BarInterval[]
    textOffsetPct: number
  }
}
